import {Zone, ZoneName, ZoneNameId} from '@app/graphql-typings/graphql';
import {forkJoin, Observable, of, Subject} from 'rxjs';
import {SiteInfo} from './../../services/api/access-sites.interface';
import {SiteService} from '@shared/services/api/site.service';
import {AceSelectComponent} from './../ace-select/ace-select.component';
import {LevelZoneService} from '@app/layout/sites/manage/manage-sites/upsert-site/forms/level-zone-form/level-zone.service';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {Component, EventEmitter, forwardRef, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {finalize} from "rxjs/operators";

@Component({
  selector: 'app-zonenames-select',
  templateUrl: './zonenames-select.component.html',
  styleUrls: ['./zonenames-select.component.scss'],
  providers: [
    {provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => ZonenamesSelectComponent), multi: true},
  ],
  standalone: false
})
export class ZonenamesSelectComponent implements OnInit, OnDestroy, ControlValueAccessor, OnChanges {

  @ViewChild('zonenameSelect', { static: true }) zonenameSelect: AceSelectComponent;
  @Input() bindToZoneNameId = false;
  @Input() siteIds: number[];
  @Input() zones: Zone[];
  @Input() multiSelect = false;
  @Input() showSite = false;
  @Input() appendTo: string;
  @Input() hideExcluded = false;
  @Input() showSelectAll = false;
  @Input() placeholder: string;
  @Input() selectAllOnSiteChange = false;
  @Input() maxSelectedLabels = 3;
  @Input() loading = false;

  @Output() loadedZoneNames = new EventEmitter<ZoneName[]>();
  @Output() openEvent = new EventEmitter<void>();
  @Output() closeEvent = new EventEmitter<void>();

  zonenames: ZoneName[];
  zonesInitialised = false;

  private readonly ngUnsubscribe: Subject<void> = new Subject();
  private onChange = (_: any) => {};
  onTouched = () => {};

  constructor(
    private levelZoneService: LevelZoneService,
    private siteService: SiteService,
  ) {
  }

  ngOnInit() {
    if (!this.multiSelect && this.showSelectAll) {
      throw new Error('multiSelect is false so cannot have showSelectAll true');
    }

    if (!this.multiSelect && this.selectAllOnSiteChange) {
      throw new Error('multiSelect is false so cannot have selectAllOnSiteChange true');
    }

    if (!this.zonesInitialised) {
      this.loadSitesAndZoneNames();
    }
    this.zonenameSelect.registerOnChange(this.aceSelectOnChange);
    this.zonenameSelect.registerOnTouched(this.aceSelectOnTouched);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.zones || changes.siteIds) {
      this.zonesInitialised = true;
      this.loadSitesAndZoneNames();
    }
  }

  private aceSelectOnChange = (selected: any) => {
    this.onChange(selected);
  };

  private aceSelectOnTouched = () => {
    this.onTouched();
  };

  private loadSitesAndZoneNames() {
    if (this.siteIds) {
      this.loading = true;
      const sites$: Observable<SiteInfo[]> = this.showSite ? this.siteService.getCachedSiteInfos(this.siteIds) : of([]);
      let zonenames$: Observable<Zone[]>;
      if (this.zones == null) {
        zonenames$ = this.levelZoneService.getZonesBySiteIds(this.siteIds);
      } else {
        zonenames$ = of(this.zones);
      }
      forkJoin([sites$, zonenames$]).pipe(finalize(() => this.loading = false)).subscribe(([siteInfos, zones]: [SiteInfo[], Zone[]]) => {
        if (zones && zones.length > 0) {
          const filteredZones = this.hideExcluded ? this.removeExcludedZones(zones) : zones;
          this.zonenames = this.addSiteNameToZonenames(this.getZonenamesFromZones(filteredZones), siteInfos);
          this.zonenames.sort(this.sortZonenamesByName);
          this.loadedZoneNames.next(this.zonenames);
          if (this.selectAllOnSiteChange) {
            this.selectZoneNames(this.zonenames);
          }
        }
      });
    } else {
      this.zonenames = null;
      this.writeValue(null);
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  writeValue(value: ZoneName | ZoneName[] | ZoneNameId | ZoneNameId[]): void {
    if (value && Array.isArray(value) && !this.multiSelect) {
      throw new Error('multiSelect is false so expected single object not array');
    } else if (value && !Array.isArray(value) && this.multiSelect) {
      throw new Error('multiSelect is true so expected array not single object');
    } else {
      this.zonenameSelect.writeValue(value);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.zonenameSelect.setDisabledState(isDisabled);
  }

  onOpen(): void {
    this.openEvent.emit();
  }

  onClose(): void {
    this.closeEvent.emit();
  }

  private selectZoneNames(zoneNames: ZoneName[]) {
    this.writeValue(this.bindToZoneNameId ? zoneNames.map(zn => zn.zone_name_id) : zoneNames);
  }

  selectAll(event) {
    event.preventDefault();
    this.selectZoneNames(this.zonenames);
  }

  private addSiteNameToZonenames(zonenames: ZoneName[], siteInfos: SiteInfo[]): ZoneName[] {
    zonenames.forEach((zonename: ZoneName) => {
      const siteInfo: SiteInfo = siteInfos.find(s => s.site_id === zonename.site_id);
      zonename['site_name'] = siteInfo ? siteInfo.site_name : null;
    });
    return zonenames;
  }

  private removeExcludedZones(zones: Zone[]): Zone[] {
    return zones.filter(z => !z.thermalcomfort_excluded);
  }

  private getZonenamesFromZones(zones: Zone[]): ZoneName[] {
    const zonenames: ZoneName[] = [];
    zones.forEach((zone: Zone) => {
      if (zone.zone_name) {
        if (!zonenames.find(z => z.zone_name_id === zone.zone_name.zone_name_id)) {
          zonenames.push(zone.zone_name);
        }
      }
    });
    return zonenames;
  }

  private sortZonenamesByName = (a: ZoneName, b: ZoneName) => a.zone_name.trim().localeCompare(b.zone_name.trim());

}
